import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, Link2, FileText, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { tagColorClasses } from "./Sidebar";
import AuthorsEditor, { authorsToRows, rowsToAuthors } from "./AuthorsEditor";
import { fetchMetadata, createCitation } from "@/lib/api";

const emptyMeta = {
  title: "",
  authors: [],
  year: "",
  journal: "",
  publisher: "",
  volume: "",
  issue: "",
  pages: "",
  doi: "",
  url: "",
  abstract: "",
  type: "article",
};

export default function AddCitationDialog({ open, setOpen, folders, tags, onCreated }) {
  const [query, setQuery] = useState("");
  const [fetching, setFetching] = useState(false);
  const [meta, setMeta] = useState(null); // null until fetched
  const [authorRows, setAuthorRows] = useState([{ first: "", last: "" }]);
  const [folderId, setFolderId] = useState("none");
  const [tagIds, setTagIds] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  // Whenever meta.authors changes from an external source (fetch), rebuild rows
  useEffect(() => {
    if (meta) {
      setAuthorRows(authorsToRows(meta.authors));
    }
  }, [meta?.authors ? meta.authors.join("|") : "__none__"]);

  const reset = () => {
    setQuery("");
    setMeta(null);
    setAuthorRows([{ first: "", last: "" }]);
    setFolderId("none");
    setTagIds([]);
    setNotes("");
    setManualMode(false);
    setFetching(false);
    setSaving(false);
  };

  const handleClose = (v) => {
    setOpen(v);
    if (!v) setTimeout(reset, 200);
  };

  const handleFetch = async () => {
    if (!query.trim()) return toast.error("Paste a URL or DOI first");
    setFetching(true);
    try {
      const res = await fetchMetadata(query.trim());
      setMeta({ ...emptyMeta, ...res.data });
      toast.success(`Metadata fetched from ${res.source}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not fetch metadata. Try manual entry.";
      toast.error(msg);
      setMeta({ ...emptyMeta, url: query.startsWith("http") ? query : "", doi: query.startsWith("http") ? "" : query });
      setManualMode(true);
    } finally {
      setFetching(false);
    }
  };

  const updateMeta = (patch) => setMeta((m) => ({ ...m, ...patch }));

  const handleSave = async () => {
    if (!meta?.title?.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = {
        ...meta,
        authors: rowsToAuthors(authorRows),
        folder_id: folderId === "none" ? null : folderId,
        tag_ids: tagIds,
        notes,
      };
      const created = await createCitation(payload);
      onCreated(created);
      toast.success("Citation saved to library");
      handleClose(false);
    } catch (err) {
      toast.error("Failed to save citation");
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (id) =>
    setTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="add-citation-dialog"
        className="max-w-2xl bg-[#FFF5FA] dark:bg-[#3B2350] border-[#F5D0E3] dark:border-[#45325A] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#3B2350] dark:text-[#FBEFF6]">
            Add a citation
          </DialogTitle>
          <DialogDescription className="text-[#5E4B70] dark:text-[#BBA9CF]">
            Paste a DOI or paper URL to auto-fetch, or enter the details manually.
          </DialogDescription>
        </DialogHeader>

        {/* Fetch input */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-[#7A6A8C]">URL or DOI</Label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A8C]" />
              <Input
                data-testid="add-citation-input-url-doi"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !fetching && handleFetch()}
                placeholder="10.1038/s41586-020-2649-2 or https://arxiv.org/abs/..."
                className="pl-9 bg-white dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A]"
              />
            </div>
            <Button
              data-testid="add-citation-fetch-metadata-button"
              onClick={handleFetch}
              disabled={fetching || !query.trim()}
              className="bg-[#C9286F] hover:bg-[#B8246C] dark:bg-[#C4A1FF] dark:hover:bg-[#A982F0] text-white gap-2"
            >
              {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {fetching ? "Fetching…" : "Fetch"}
            </Button>
          </div>
          {!meta && (
            <div className="flex items-center gap-3 pt-1">
              <div className="flex-1 h-px bg-[#F5D0E3] dark:bg-[#45325A]" />
              <span className="text-xs uppercase tracking-widest text-[#7A6A8C] font-medium">or</span>
              <div className="flex-1 h-px bg-[#F5D0E3] dark:bg-[#45325A]" />
            </div>
          )}
          {!meta && (
            <Button
              data-testid="add-citation-manual-mode"
              variant="outline"
              onClick={() => {
                setMeta({ ...emptyMeta });
                setManualMode(true);
              }}
              className="w-full gap-2 border-dashed border-[#F5D0E3] dark:border-[#45325A] bg-white/60 dark:bg-[#1E1529] hover:bg-[#FCE7F3] dark:hover:bg-[#2E2140] text-[#5E4B70] dark:text-[#BBA9CF]"
            >
              <Pencil className="w-4 h-4" /> Enter citation manually
            </Button>
          )}
        </div>

        {/* Metadata preview / editor */}
        {meta && (
          <div className="space-y-4 fade-slide-in mt-2">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wide text-[#7A6A8C] font-medium">
                {manualMode ? "Enter details" : "Fetched metadata"}
              </div>
              <button
                onClick={() => setManualMode((v) => !v)}
                className="text-xs text-[#C9286F] dark:text-[#C4A1FF] hover:underline"
                data-testid="toggle-manual-edit"
              >
                {manualMode ? "Hide fields" : "Edit fields"}
              </button>
            </div>

            <div className="p-4 rounded-lg bg-white dark:bg-[#1E1529] border border-[#F5D0E3] dark:border-[#45325A] space-y-3">
              <div>
                <Label className="text-xs text-[#7A6A8C]">Title</Label>
                <Input
                  data-testid="meta-title-input"
                  value={meta.title || ""}
                  onChange={(e) => updateMeta({ title: e.target.value })}
                  className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350] font-serif-display text-base"
                />
              </div>
              <div className="grid grid-cols-[1fr,120px] gap-3">
                <AuthorsEditor rows={authorRows} onChange={setAuthorRows} />
                <div>
                  <Label className="text-xs text-[#7A6A8C]">Year</Label>
                  <Input
                    data-testid="meta-year-input"
                    value={meta.year || ""}
                    onChange={(e) => updateMeta({ year: e.target.value })}
                    className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]"
                  />
                </div>
              </div>

              {manualMode && (
                <div className="grid grid-cols-2 gap-3 fade-slide-in">
                  <div>
                    <Label className="text-xs text-[#7A6A8C]">Journal / Source</Label>
                    <Input
                      value={meta.journal || ""}
                      onChange={(e) => updateMeta({ journal: e.target.value })}
                      className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#7A6A8C]">Publisher</Label>
                    <Input
                      value={meta.publisher || ""}
                      onChange={(e) => updateMeta({ publisher: e.target.value })}
                      className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#7A6A8C]">Volume</Label>
                    <Input value={meta.volume || ""} onChange={(e) => updateMeta({ volume: e.target.value })} className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#7A6A8C]">Issue</Label>
                    <Input value={meta.issue || ""} onChange={(e) => updateMeta({ issue: e.target.value })} className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#7A6A8C]">Pages</Label>
                    <Input value={meta.pages || ""} onChange={(e) => updateMeta({ pages: e.target.value })} className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#7A6A8C]">DOI</Label>
                    <Input value={meta.doi || ""} onChange={(e) => updateMeta({ doi: e.target.value })} className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-[#7A6A8C]">URL</Label>
                    <Input value={meta.url || ""} onChange={(e) => updateMeta({ url: e.target.value })} className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-[#7A6A8C]">Type</Label>
                    <Select value={meta.type} onValueChange={(v) => updateMeta({ type: v })}>
                      <SelectTrigger className="mt-1 bg-[#FFF5FA] dark:bg-[#3B2350]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="article">Journal Article</SelectItem>
                        <SelectItem value="book">Book</SelectItem>
                        <SelectItem value="conference">Conference Paper</SelectItem>
                        <SelectItem value="webpage">Webpage</SelectItem>
                        <SelectItem value="preprint">Preprint</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {meta.abstract && !manualMode && (
                <div>
                  <Label className="text-xs text-[#7A6A8C]">Abstract</Label>
                  <p className="mt-1 text-sm text-[#5E4B70] dark:text-[#BBA9CF] leading-relaxed line-clamp-3">
                    {meta.abstract}
                  </p>
                </div>
              )}

              {(meta.journal || meta.publisher) && !manualMode && (
                <div className="flex flex-wrap gap-2 text-xs text-[#5E4B70] dark:text-[#BBA9CF]">
                  {meta.journal && <span>📖 {meta.journal}</span>}
                  {meta.publisher && <span>· {meta.publisher}</span>}
                  {meta.volume && <span>· Vol. {meta.volume}</span>}
                  {meta.pages && <span>· pp. {meta.pages}</span>}
                </div>
              )}
            </div>

            {/* Organize */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-[#7A6A8C]">Project</Label>
                <Select value={folderId} onValueChange={setFolderId}>
                  <SelectTrigger data-testid="citation-folder-select" className="mt-1 bg-white dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unfiled</SelectItem>
                    {folders.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-[#7A6A8C]">Tags</Label>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="citation-tag-picker">
                  {tags.length === 0 && <span className="text-xs text-[#7A6A8C]">No tags yet — create them in the sidebar.</span>}
                  {tags.map((t) => {
                    const active = tagIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        onClick={() => toggleTag(t.id)}
                        className={`text-xs px-2 py-1 rounded-full transition-all ${tagColorClasses(t.color)} ${active ? "ring-2 ring-[#C9286F] dark:ring-[#C4A1FF]" : "opacity-80"}`}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-[#7A6A8C]">Notes / Quotes</Label>
              <Textarea
                data-testid="citation-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Key insights, quotes, or your own thoughts…"
                className="mt-1 bg-white dark:bg-[#1E1529] border-[#F5D0E3] dark:border-[#45325A] min-h-[80px]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                data-testid="add-citation-save-button"
                onClick={handleSave}
                disabled={saving}
                className="bg-[#C9286F] hover:bg-[#B8246C] dark:bg-[#C4A1FF] dark:hover:bg-[#A982F0] text-white gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save to library
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
